import { useQuery, useMutation, useQueryClient } from 'react-query';
import { toast } from 'react-toastify';
import useAuthStore from '../../store/authStore';
import { createSeries, deleteSeries, getSeries, getSeriesById, getSeriesByType, updateSeries } from '../../api/admin/adminSeries';
import { useNavigate } from 'react-router-dom';

export const useSeries = () => {
  const { setError } = useAuthStore();
  
  const {
    data: series,
    isLoading,
    error,
    refetch
  } = useQuery('series', getSeries, {
    onError: (error) => {
      setError(error?.response?.data?.message || 'Failed to fetch series');
    },
    retry: false,
    staleTime: 300000, // 5 minutes
    cacheTime: 3600000, // 1 hour
  });

  return {
    series,
    isLoading,
    error,
    refetchSeries: refetch
  };
};

export const useSeriesById = (id) => {
  const { setError } = useAuthStore();
  
  const {
    data: series,
    isLoading,
    error
  } = useQuery(['series', id], () => getSeriesById(id), {
    onError: (error) => {
      setError(error?.response?.data?.message || 'Failed to fetch series');
    },
    retry: false,
    enabled: !!id,
  });

  return {
    series,
    isLoading,
    error
  };
};

export const useCreateSeries = () => {
  const queryClient = useQueryClient();
  const { setError } = useAuthStore();
  const navigate = useNavigate();
  
  const mutation = useMutation(createSeries, {
    onSuccess: (data) => {
      toast.success('Series created successfully!', {
        position: "top-right",
        autoClose: 3000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
      });
      
      // Invalidate all series queries
      queryClient.invalidateQueries('series');
      
      // Update all seriesByType queries
      queryClient.invalidateQueries(['seriesByType']);
      
      // Alternatively, if you know the type of the created series:
      // const seriesType = data.type; // Assuming the response contains the type
      // queryClient.invalidateQueries(['seriesByType', seriesType]);
      
      navigate(-1);
    },
    onError: (error) => {
      const errorMessage = error?.response?.data?.message || 'Failed to create series';
      setError(errorMessage);
      toast.error(errorMessage);
    },
  });
  
  return {
    createSeries: mutation.mutate,
    isLoading: mutation.isLoading,
    isError: mutation.isError,
    error: mutation.error
  };
};

export const useUpdateSeries = () => {
  const queryClient = useQueryClient();
  const { setError } = useAuthStore();

  const mutation = useMutation(updateSeries, {
    onSuccess: (data, variables) => {
      toast.success('Series updated successfully!', {
        position: "top-right",
        autoClose: 3000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
      });
      queryClient.invalidateQueries('series');
      queryClient.invalidateQueries(['series', variables.id]);
    },
    onError: (error) => {
      const errorMessage = error?.response?.data?.message || 'Failed to update series';
      setError(errorMessage);
      toast.error(errorMessage);
    },
  });

  return {
    updateSeries: mutation.mutate,
    isLoading: mutation.isLoading,
    isError: mutation.isError,
    error: mutation.error
  };
};

export const useDeleteSeries = () => {
  const queryClient = useQueryClient();
  const { setError } = useAuthStore();

  const mutation = useMutation(deleteSeries, {
    onSuccess: () => {
      toast.success('Series deleted successfully!', {
        position: "top-right",
        autoClose: 3000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
      });
      queryClient.invalidateQueries('series');
    },
    onError: (error) => {
      const errorMessage = error?.response?.data?.message || 'Failed to delete series';
      setError(errorMessage);
      toast.error(errorMessage);
    },
  });

  return {
    deleteSeries: mutation.mutate,
    isLoading: mutation.isLoading,
    isError: mutation.isError,
    error: mutation.error
  };
};

export const useSeriesByType = (series_type) => {
  const { setError } = useAuthStore();
  
  const {
    data: series,
    isLoading,
    error,
    refetch
  } = useQuery(['seriesByType', series_type], () => getSeriesByType(series_type), {
    onError: (error) => {
      setError(error?.response?.data?.message || `Failed to fetch ${series_type} series`);
    },
    retry: false,
    staleTime: 300000, // 5 minutes
    cacheTime: 3600000, // 1 hour
    enabled: !!series_type // Only run the query if series_type is provided
  });
  
  return {
    series,
    isLoading,
    error,
    refetchSeries: refetch
  };
};